import { useCallback, useEffect, useMemo, useState } from "react";

import {
    BORDER_RADIUS_SM,
    Button,
    Column,
    FormInput,
    IconButton,
    Row,
    StatColumn,
    TagTextAlert,
    Text,
    ToggleButtons,
    TooltipText,
    useAppPalette
} from "@bridgesplit/ui";
import { formatPercent, percentUiToDecimals } from "@bridgesplit/utils";
import { BsMetaUtil, MAX_TICK_INDEX, MIN_TICK_INDEX } from "@bridgesplit/abf-react";
import debounce from "lodash.debounce";
import { PriceRangeStatus, YieldToggle } from "app/components/common";
import { AddOutlined, RemoveOutlined } from "@mui/icons-material";
import { SqrtPriceMath } from "@raydium-io/raydium-sdk-v2";
import Decimal from "decimal.js";

import { useRaydiumContext } from "../RaydiumContext";
import {
    calculateRaydiumDepositRatios,
    flipPriceIfNeeded,
    getPriceFromTick,
    getTickFromPrice,
    useFormatRaydiumPercentChangeFromCurrent,
    useRaydiumFormatPrice
} from "../util";
import { PRICE_RANGE_DECIMALS } from "../common";
import { OpenPositionForm } from "../types";
import { useOpenPositionYieldData } from "./util";
import { getActionTypeInitalized } from "../../clmm/util";

enum Mode {
    Custom = 0,
    Full = 1
}

export default function OpenPositionPriceRange() {
    const { openPositionForm, setOpenPositionForm, positionData, isActionTypeInitialized } = useRaydiumContext();
    const updateAmountsIfNeeded = useUpdateAmountsIfNeeded();

    const mode =
        openPositionForm.tickLower === MIN_TICK_INDEX && openPositionForm.tickUpper === MAX_TICK_INDEX
            ? Mode.Full
            : Mode.Custom;

    return (
        <>
            <ToggleButtons
                value={mode}
                options={[
                    {
                        value: Mode.Full,
                        label: "Full",
                        disabled: !getActionTypeInitalized("open", isActionTypeInitialized)
                    },
                    {
                        value: Mode.Custom,
                        label: "Custom",
                        disabled: !getActionTypeInitalized("open", isActionTypeInitialized)
                    }
                ]}
                setValue={(mode) => {
                    const wasOriginallyFullRange =
                        positionData?.tickLower === MIN_TICK_INDEX && positionData?.tickUpper === MAX_TICK_INDEX;

                    const { tickLower, tickUpper } = (() => {
                        if (mode === Mode.Full) return { tickLower: MIN_TICK_INDEX, tickUpper: MAX_TICK_INDEX };
                        if (mode === Mode.Custom && wasOriginallyFullRange)
                            return { tickLower: undefined, tickUpper: undefined };
                        return { tickLower: positionData?.tickLower, tickUpper: positionData?.tickUpper };
                    })();

                    setOpenPositionForm((prev) =>
                        updateAmountsIfNeeded({
                            ...prev,
                            tickLower,
                            tickUpper,
                            status: "refetch-needed"
                        })
                    );
                }}
            />
            {mode === Mode.Custom && (
                <Column spacing={1}>
                    <Edit />
                    <Buttons />
                </Column>
            )}
            <Error />

            <Stats />
        </>
    );
}

function Edit() {
    const {
        openPositionForm,
        setOpenPositionForm,
        isFlippedPrice,
        raydiumPosition,
        poolData,
        isActionTypeInitialized
    } = useRaydiumContext();
    const [localPrice, setLocalPrice] = useState<number | undefined>(undefined);
    const [focused, setFocused] = useState<"tickLower" | "tickUpper" | null>(null);
    const updateAmountsIfNeeded = useUpdateAmountsIfNeeded();

    const getFormPrice = useCallback(
        (property: "tickLower" | "tickUpper") => {
            const formValue = openPositionForm[property];
            if (formValue === undefined) return undefined;
            return flipPriceIfNeeded(getPriceFromTick(formValue, raydiumPosition), isFlippedPrice);
        },
        [openPositionForm, raydiumPosition, isFlippedPrice]
    );

    const setTick = useCallback(
        (price: number | undefined, property: "tickLower" | "tickUpper") => {
            const flippedPrice = flipPriceIfNeeded(price, isFlippedPrice);
            const tick =
                flippedPrice && poolData?.tickSpacing
                    ? getTickFromPrice(flippedPrice, poolData.tickSpacing, raydiumPosition)
                    : undefined;
            setOpenPositionForm((prev) =>
                updateAmountsIfNeeded({
                    ...prev,
                    [property]: tick,
                    status: "refetch-needed"
                })
            );
        },
        [isFlippedPrice, poolData?.tickSpacing, raydiumPosition, setOpenPositionForm, updateAmountsIfNeeded]
    );

    const formatPercentChange = useFormatRaydiumPercentChangeFromCurrent();

    // inputs are flipped if price is flipped
    const inputs: [string, "tickLower" | "tickUpper"][] = isFlippedPrice
        ? [
              ["Min price", "tickUpper"],
              ["Max price", "tickLower"]
          ]
        : [
              ["Min price", "tickLower"],
              ["Max price", "tickUpper"]
          ];

    const setTickDebounced = useMemo(() => debounce(setTick, 400), [setTick]);

    const updateTick = useCallback(
        (change: number, property: "tickLower" | "tickUpper") => {
            if (!poolData) return;
            setOpenPositionForm((prev) => {
                const prevTick = prev[property];
                if (prevTick === undefined) return prev;
                const changeTick = change * (poolData.tickSpacing ?? 0);
                return updateAmountsIfNeeded({
                    ...prev,
                    [property]: prevTick + changeTick,
                    status: "refetch-needed"
                });
            });
        },
        [poolData, setOpenPositionForm, updateAmountsIfNeeded]
    );

    useEffect(() => {
        return () => {
            setTickDebounced.cancel();
        };
    }, [setTickDebounced]);

    const arrows: [JSX.Element, number][] = isFlippedPrice
        ? [
              [<AddOutlined />, -1],
              [<RemoveOutlined />, 1]
          ]
        : [
              [<AddOutlined />, 1],
              [<RemoveOutlined />, -1]
          ];

    return (
        <Row spacing={1}>
            {inputs.map(([label, property]) => {
                const isFocused = focused === property;
                const value = isFocused ? localPrice : getFormPrice(property);

                return (
                    <Column spacing={0.5} key={label + property} color="body">
                        <Row spaceBetween>
                            <Text color="caption" variant="body2">
                                {label}
                            </Text>
                            {poolData && (
                                <TooltipText helpText="Percent change from current" color="caption" variant="body2">
                                    {formatPercentChange(openPositionForm[property], poolData.tickCurrent)}
                                </TooltipText>
                            )}
                        </Row>
                        <FormInput
                            disabled={!getActionTypeInitalized("open", isActionTypeInitialized)}
                            sx={{ ".MuiOutlinedInput-root": { pr: 1 } }}
                            inputHeight={50}
                            InputProps={{
                                endAdornment: (
                                    <Column spacing={0.5}>
                                        {arrows.map(([icon, change]) => (
                                            <IconButton
                                                sx={{ p: 0.25, borderRadius: BORDER_RADIUS_SM }}
                                                textVariant="caption"
                                                jsxIcon={icon}
                                                onClick={() => updateTick(change, property)}
                                                disabled={!getActionTypeInitalized("open", isActionTypeInitialized)}
                                            />
                                        ))}
                                    </Column>
                                )
                            }}
                            onFocus={() => {
                                setFocused(property);
                                setLocalPrice(getFormPrice(property));
                            }}
                            onBlur={() => setFocused(null)}
                            decimals={PRICE_RANGE_DECIMALS}
                            placeholder="0"
                            value={value}
                            setValue={(newValue) => {
                                // prevent unnecessary re-renders
                                if (!isFocused) return;
                                setLocalPrice(newValue);
                                setTickDebounced(newValue, property);
                            }}
                            variant="number"
                        />
                    </Column>
                );
            })}
        </Row>
    );
}

const PERCENT_DIFFS = [1, 5, 10];
function Buttons() {
    const { openPositionForm, setOpenPositionForm, positionData, poolData, raydiumPosition, isActionTypeInitialized } =
        useRaydiumContext();
    const { textSecondary, hoverBackground } = useAppPalette();
    const updateAmountsIfNeeded = useUpdateAmountsIfNeeded();
    const tickLower = positionData?.tickLower ?? MIN_TICK_INDEX;
    const tickUpper = positionData?.tickUpper ?? MAX_TICK_INDEX;

    const options: [string, number, number][] = [["Reset", tickLower, tickUpper]];

    PERCENT_DIFFS.forEach((percentUi) => {
        const percentDecimals = percentUiToDecimals(percentUi);

        const currentPrice = getPriceFromTick(poolData?.tickCurrent ?? 0, raydiumPosition);

        const lowerPrice = currentPrice * (1 - percentDecimals);
        const upperPrice = currentPrice * (1 + percentDecimals);

        const subtract = getTickFromPrice(lowerPrice, poolData?.tickSpacing ?? 0, raydiumPosition);
        const add = getTickFromPrice(upperPrice, poolData?.tickSpacing ?? 0, raydiumPosition);

        const tickLower = Math.min(add, subtract);
        const tickUpper = Math.max(add, subtract);

        options.push([`±  ${formatPercent(percentUi, { skipMultiplication: true })}`, tickLower, tickUpper]);
    });

    return (
        <Row spacing={1}>
            {options.map(([label, tickLower, tickUpper]) => {
                const isSelected =
                    openPositionForm.tickLower === tickLower &&
                    openPositionForm.tickUpper === tickUpper &&
                    label !== "Reset";
                return (
                    <Button
                        disabled={!getActionTypeInitalized("open", isActionTypeInitialized)}
                        key={label}
                        variant="outlined"
                        textProps={{ variant: "body2", sx: { color: isSelected ? undefined : textSecondary } }}
                        sx={{ background: isSelected ? hoverBackground : undefined }}
                        height={30}
                        fullWidth
                        onClick={() =>
                            setOpenPositionForm((prev) =>
                                updateAmountsIfNeeded({
                                    ...prev,
                                    tickLower,
                                    tickUpper,
                                    status: "refetch-needed"
                                })
                            )
                        }
                    >
                        {label}
                    </Button>
                );
            })}
        </Row>
    );
}

const variant = "body2";
function Stats() {
    const { poolData, raydiumPosition, openPositionForm } = useRaydiumContext();
    const depositRatio = useInitialRaydiumRatio();
    const yieldData = useOpenPositionYieldData();
    const formatPrice = useRaydiumFormatPrice();

    if (
        openPositionForm.tickLower === undefined ||
        openPositionForm.tickUpper === undefined ||
        !depositRatio ||
        openPositionForm.status === "error"
    )
        return null;

    return (
        <StatColumn
            loading={!yieldData || openPositionForm.status === "refetching"}
            variant={variant}
            stats={[
                {
                    caption: "Estimated yield",
                    value: yieldData ? <YieldToggle variant={variant} yieldData={yieldData} /> : undefined
                },

                {
                    caption: "Deposit ratio",
                    value: `${formatPercent(depositRatio?.ratioA)} ${BsMetaUtil.getSymbol(
                        raydiumPosition.tokenA.metadata
                    )} / ${formatPercent(depositRatio?.ratioB)} ${BsMetaUtil.getSymbol(
                        raydiumPosition.tokenB.metadata
                    )}`
                },
                {
                    caption: "Current price",
                    value: poolData ? (
                        <Row spacing={0.5}>
                            <PriceRangeStatus
                                tickCurrentIndex={poolData.tickCurrent}
                                tickLower={openPositionForm.tickLower}
                                tickUpper={openPositionForm.tickUpper}
                                variant={variant}
                            />
                            <Text variant={variant}>{formatPrice(poolData.tickCurrent)}</Text>
                        </Row>
                    ) : undefined
                }
            ]}
        />
    );
}

function Error() {
    const { openPositionForm } = useRaydiumContext();
    if (openPositionForm.status !== "error") return null;
    return (
        <TagTextAlert icon="warning" color="error">
            Invalid price range
        </TagTextAlert>
    );
}

export function useInitialRaydiumRatio() {
    const { raydiumPosition, poolData, positionData } = useRaydiumContext();
    return useMemo(() => {
        if (!poolData || !positionData) return undefined;
        const priceDecimals = new Decimal(poolData.currentPrice);
        const sqrtPrice = SqrtPriceMath.priceToSqrtPriceX64(
            priceDecimals,
            raydiumPosition.tokenA.metadata.decimals,
            raydiumPosition.tokenB.metadata.decimals
        );

        return calculateRaydiumDepositRatios(
            sqrtPrice,
            raydiumPosition.tokenA.metadata.decimals,
            raydiumPosition.tokenB.metadata.decimals,
            positionData.tickLower,
            positionData.tickUpper
        );
    }, [poolData, positionData, raydiumPosition.tokenA.metadata.decimals, raydiumPosition.tokenB.metadata.decimals]);
}

function useUpdateAmountsIfNeeded() {
    const { raydiumPosition, poolData } = useRaydiumContext();
    return useCallback(
        (prev: OpenPositionForm) => {
            // if amount has been adjusted, don't override
            if (prev.side !== undefined || !poolData || prev.tickLower === undefined || prev.tickUpper === undefined)
                return prev;

            const ratio = calculateRaydiumDepositRatios(
                poolData.sqrtPriceX64,
                raydiumPosition.tokenA.metadata.decimals,
                raydiumPosition.tokenB.metadata.decimals,
                prev.tickLower,
                prev.tickUpper
            );

            if (!ratio) return prev;

            // otherwise, user updated price range without amounts and expects USD amount to remain the same
            const originalUsdValue = raydiumPosition.totalPrice;

            const tokenAAmountUsd = originalUsdValue * ratio.ratioA;
            const tokenBAmountUsd = originalUsdValue * ratio.ratioB;

            return {
                ...prev,
                tokenAAmount: tokenAAmountUsd / raydiumPosition.tokenA.usdPrice,
                tokenBAmount: tokenBAmountUsd / raydiumPosition.tokenB.usdPrice
            };
        },
        [
            poolData,
            raydiumPosition.tokenA.metadata.decimals,
            raydiumPosition.tokenA.usdPrice,
            raydiumPosition.tokenB.metadata.decimals,
            raydiumPosition.tokenB.usdPrice,
            raydiumPosition.totalPrice
        ]
    );
}
